<template>
  <div v-if="displayShowcaseContainer">
    <div class="col-md-12">
      <div class="panel profile-panel profile-showcases">
        <template v-if="showcases.length">
          <h3 class="text-center tw-mb-2" v-html="$t('wi.showcases_games_events')"></h3>
          <div class="tw-w-full tw-text-center">
            <img src="/img/logos/powered-by-blast-iq.svg" alt="Blast IQ Logo">
          </div>
          <div id="showcases">
            <draggable
              @end="arrangeShowcases"
              v-model="showcases"
              :options="{ disabled: roles.is_public, forceFallback: true }"
            >
              <div
                v-for="(showcase, index) in showcases"
                :class="{ 'tw-cursor-move': !roles.is_public }"
              >
                <div
                  class="tw-flex tw-justify-between tw-border-b tw-border-grey-light"
                >
                  <div class="tw-flex tw-flex-col tw-mb-6">
                    <h4 v-text="showcase.info.name"></h4>
                    <p>
                      {{ showcase.info.city_state }}
                      <span v-if="showcase.info.dateString">|</span>
                      {{ showcase.info.dateString }}
                    </p>
                    <p v-text="showcase.info.comments"></p>
                  </div>
                  <div class="tw-flex tw-justify-end tw-items-center">
                    <delete-button
                      :item-id="showcase.id"
                      type="showcases"
                      :message="
                        `${$t('wi.sure_to_delete')} ${
                          showcase.info.name
                        } ${$t('wi.showcase_from_profile')}`
                      "
                    />
                    <edit-button
                      modal-target="editShowcase"
                      :modal="{
                        title: $t('wi.customize_showcase'),
                        index,
                        preserveData: false
                      }"
                    />
                  </div>
                </div>
                <blast-simple-tabs
                  :justified="true"
                  v-if="showcase.actions || showcase.videos.length > 0"
                >
                  <blast-tab
                    :label="$t('wi.averages')"
                    id="showcase-averages"
                    :is-active="true"
                    v-if="showcase.actions"
                  >
                    <div
                      class="tw-flex tw-flex-wrap tw-justify-center lg:tw-justify-start tw-mb-10"
                    >
                      <div
                        v-for="action in showcase.actions.actions"
                        :key="action.label"
                        class="stat tw-w-full tw-mb-4 tw-mx-2 tw-h-32 stat-left"
                        :class="{ highlight: action.highlighted }"
                        v-if="action.visible && parseFloat(action.average)"
                      >
                        <i class="fas fa-star pull-right"></i>
                        <span
                          class="stat-name tw-mb-4"
                          v-html="action.name"
                        ></span
                        ><br />
                        <p
                          class="tw-flex tw-flex-col"
                          v-if="action.score_metric"
                        >
                          <span
                            class="small-stat tw-leading-none tw-mb-2"
                            v-html="action.score"
                          ></span>
                          <progress-bar :progress="action.score"></progress-bar>
                        </p>
                        <p class="tw-flex tw-justify-between" v-else>
                          <span
                            class="small-stat tw-leading-none"
                            v-html="action.average"
                          ></span>
                          <span
                            class="text-muted stat-unit tw-inline-flex tw-items-end"
                            v-html="actionUnit(action)"
                          ></span>
                        </p>
                      </div>
                    </div>
                  </blast-tab>

                  <blast-tab
                    :label="$t('wi.videos')"
                    id="showcase-videos"
                    class="showcase-videos"
                    v-if="showcase.videos.length > 0"
                  >
                    <swiper ref="slider" :options="carouselOptions" class="swiper">
                      <swiper-slide
                          v-for="video in showcase.videos"
                          :key="randomInteger(video.id)"
                      >
                        <highlight-video
                            :video="video"
                            :key="randomInteger(video.id)"
                            :no-actions="true"
                            @preview="preview"
                        />
                      </swiper-slide>
                      <div class="swiper-pagination" slot="pagination"></div>
                    </swiper>
                    <div class="showcase-videos__navigation showcase-videos__navigation_prev" @click="prevSlide">
                      <i class="fal fa-angle-left"></i>
                    </div>
                    <div class="showcase-videos__navigation showcase-videos__navigation_next" @click="nextSlide">
                      <i class="fal fa-angle-right"></i>
                    </div>
                  </blast-tab>
                </blast-simple-tabs>
              </div>
            </draggable>
          </div>
          <add-button
            modal-target="editShowcase"
            v-if="roles.is_owner"
            :modal="{ title: $t('wi.add_showcase') }"
          ></add-button>
        </template>
        <empty-state
          v-else
          :title="$t('wi.showcases')"
          :description="$t('wi.show_your_best_showcase')"
          :button="{ caption: $t('wi.add_showcase') }"
          :modal="{ title: $t('wi.add_showcase') }"
          modalTarget="editShowcase"
        />
      </div>
    </div>
    <video-preview
      :visible="previewModalVisible"
      :video="previewVideo"
      @closed="clearPreview"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import EditButton from '../VisualElements/EditButton';
import DeleteButton from '../VisualElements/DeleteButton';
import AddButton from '../VisualElements/AddButton';
import draggable from 'vuedraggable';
import HighlightVideo from '../HighlightVideos/HighlightVideo';
import VideoPreview from '../VisualElements/VideoPreview';
import EmptyState from '../EmptyState';
import { Swiper, SwiperSlide } from 'vue-awesome-swiper';
import 'swiper/css/swiper.css';
import randomIntegerMixin from '../../mixins/randomInteger';
import academyMixin from '../../mixins/academy';
import ProgressBar from '../VisualElements/ProgressBar';

export default {
  name: 'BaseballAthletics',
  mixins: [randomIntegerMixin, academyMixin],
  data() {
    return {
      previewModalVisible: false,
      previewVideo: null,
      carouselOptions: {
        pagination: {
          el: '.swiper-pagination',
          clickable: true,
        },
        breakpoints: {
          1024: {
            slidesPerView: 5,
            spaceBetween: 7,
          },
          920: {
            slidesPerView: 3,
            slidesPerGroup: 3,
            spaceBetween: 7,
          },
          768: {
            slidesPerView: 2,
            slidesPerGroup: 2,
            spaceBetween: 5,
          },
          400: {
            slidesPerView: 1,
            slidesPerGroup: 1,
            spaceBetween: 2,
          },
        },
      },
    };
  },
  components: {
    EditButton,
    AddButton,
    draggable,
    HighlightVideo,
    VideoPreview,
    EmptyState,
    DeleteButton,
    ProgressBar,
    Swiper,
    SwiperSlide,
  },
  computed: {
    ...mapGetters({
      roles: 'player-profile/roles',
      showcasesData: 'player-profile/showcases',
      profileData: 'player-profile/profile'
    }),
    showcases: {
      get() {
        return this.showcasesData;
      },
      set(data) {
        this.setData({
          type: 'showcases',
          data
        });
      }
    },
    displayShowcaseContainer() {
      if (this.roles.is_public) {
        if (! this.showcases.length > 0) {
          return false;
        }
      }

      return true;
    },
    slider() {
      return this.$refs.slider[0].$swiper;
    },
  },
  methods: {
    ...mapActions({
      updateOrder: 'player-profile/updateOrder'
    }),
    ...mapMutations({
      setData: 'player-profile/setData'
    }),
    actionUnit(action) {
      const unit = action.unit;
      const ignoredUnits = ['°'];

      if (ignoredUnits.includes(unit)) {
        return '';
      }

      return unit;
    },
    arrangeShowcases() {
      let orders = this.profileData.showcases.map(showcase => {
        return { id: showcase.id };
      });

      this.updateOrder({
        type: 'showcase',
        orders
      });
    },
    clearPreview() {
      this.previewVideo = null;
    },
    preview(video) {
      this.previewVideo = video;
      this.previewModalVisible = true;
    },
    prevSlide() {
      this.slider.slidePrev();
    },
    nextSlide() {
      this.slider.slideNext();
    },
  }
};
</script>

<style lang="scss">
$padding: -20px;

.stat {
  max-width: 13.9rem;
}

.showcase-videos {
  position: relative;

  &__navigation {
    top: 50%;
    font-size: 36px;
    color: #5b9bd1;
    cursor: pointer;
    transform: translateY(-50%);

    &_next {
      position: absolute;
      right: $padding;
    }

    &_prev {
      position: absolute;
      left: $padding;
    }
  }
}

.swiper {

  &-slide {
    display: flex;
    justify-content: center;
  }

  &-pagination {
    position: relative;
    bottom: 0 !important;
  }
}
</style>
